import { AfterViewInit, Component, OnDestroy, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { UserInfo } from '@core/models/user-info.model';
import { ExtranetAuthenticationService } from '@core/services/extranet-authentication.service';
import { PasswordService } from '@core/services/password.service';
import { environment } from '@env';
import { TranslateService } from '@ngx-translate/core';
import { ApiDealerService } from '@src/app/core/services/api-dealer.service';
import { CommonService } from '@src/app/core/services/common.service';
import { ElementControlService } from '@src/app/core/services/element-control.service';
import AES from 'crypto-js/aes';
import encUtf8 from 'crypto-js/enc-utf8';
import { RecaptchaComponent } from 'ng-recaptcha';
import { Subject, Subscription, combineLatest } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { AppConfig, SystemDialogService, ToastService } from 'sigv-library';
import { isMobile, isNullOrEmpty } from 'sigv-library/util';
import { BackgroundImageService } from '../../services/background-image.service';

interface LoginForm {
  account: string;
  password?: string;
  recaptchaToken?: string;
}

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnDestroy, AfterViewInit {
  @ViewChild('loginForm', { read: NgForm }) loginForm: any;
  @ViewChild(RecaptchaComponent) recaptcha?: RecaptchaComponent;

  blobUrlLogin = '';
  blobUrlBg = '';
  loginFormModel: LoginForm;
  isExtranet = environment.isExtranet;
  currentDevice = '';
  currentUser?: UserInfo;

  isShowForgetPasswordDialog = false;
  otpType = '';
  otpShowEmail = false;
  otpShowPhone = false;

  inputBackgroundNormalState = 'rgb(255, 255, 255)';
  ////是否顯示google驗證圖形
  elementControlJSON={"Login": {
    "GoogleReCaptcha": {
      "display": false
    }
  }};
  private readonly destroy$ = new Subject();
  private resizeSubscription: Subscription;

  constructor(
    public appConfig: AppConfig,
    private extranetAuthenticationService: ExtranetAuthenticationService,
    private passwordService: PasswordService,
    private router: Router,
    private route: ActivatedRoute,
    private toastService: ToastService,
    private translateService: TranslateService,
    private systemDialogService: SystemDialogService,
    private elementControlService:ElementControlService,
    private backgroundImageService:BackgroundImageService,
    private commonService: CommonService,
    private dealerService: ApiDealerService
  ) {
    this.loginFormModel = {
      account: this.getDefaultAccount(),
    };

    this.currentDevice = isMobile() ? 'APP' : 'Web';
    

    this.resizeSubscription = this.backgroundImageService.resize$.subscribe(() => {
      this.blobUrlBg = this.backgroundImageService.blobUrlBg;
      this.blobUrlLogin = this.backgroundImageService.blobUrlLogin;
    });
  }

  ngOnInit(){
    //取得是否顯示google驗證圖形
    this.elementControlService.getElementControl()
    .pipe(takeUntil(this.destroy$))
    .subscribe((elementControlJSON)=>{
      if(isNullOrEmpty(elementControlJSON)===false){
         this.elementControlJSON = elementControlJSON;

      }
    });
  }



  ngAfterViewInit(): void {
    /**
     * @description 從背景樣式判斷帳號密碼是否為自動輸入 為了讓使用者不必的先點擊背景畫面才啟用登入按鈕
     */
    setTimeout(() => {
      const account = document.getElementById('account');
      const userPassword = document.getElementById('userPassword');
      if (account) {
        const accountStyle = window.getComputedStyle(account);
        if (
          accountStyle &&
          accountStyle.backgroundColor !== this.inputBackgroundNormalState
        ) {
          this.loginForm.controls.account.setErrors(null);
        }
      }
      if (userPassword) {
        const passwordStyle = window.getComputedStyle(userPassword);
        if (
          passwordStyle &&
          passwordStyle.backgroundColor !== this.inputBackgroundNormalState
        ) {
          this.loginForm.controls.userPassword.setErrors(null);
        }
      }
    }, 500);
    this.checkStateWithQueryParam();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.resizeSubscription.unsubscribe();
  }

  /**
   * @description 如果從忘記密碼導回登入頁時，取得忘記密碼的帳號並移除 localStorage 資料
   */
  private getDefaultAccount(): string {
    const account = localStorage.getItem('forgotPasswordAccount') ?? '';

    localStorage.removeItem('forgotPasswordAccount');

    return account;
  }

  submit(): void {
    combineLatest([
      this.passwordService.getSystemParameter('AAD.DesKey'),
      this.passwordService.getSystemParameter('AAD.DesIV'),
    ])
      .pipe(
        takeUntil(this.destroy$),
        map((response) => {
          return response.every(
            ({ data, rtnCode }) => data?.length && rtnCode === 0
          )
            ? (response
                .map(({ data }) => data?.[0]?.value)
                .filter((value) => !!value) as string[])
            : null;
        })
      )
      .subscribe((response) => {
        if (!response) {
          return;
        }

        const [desKey, desIV] = response;
        const { account, password, recaptchaToken } = this.loginFormModel;
        const key = encUtf8.parse(desKey);
        const iv = encUtf8.parse(desIV);
        const loginRequest = {
          account,
          password: password
            ? AES.encrypt(password, key ?? '', { iv }).toString()
            : '',
          recaptchaToken,
        };

        // 重置 recaptcha 值
        this.recaptcha?.reset();

        this.extranetAuthenticationService
          .login(loginRequest)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: ({ isSuccess, msg, rtnCode }) => {
              this.loginHandler(isSuccess, msg, rtnCode);
            },
            error: () => {
              this.loginHandler(false);
            },
          });
      });
  }

  loginHandler(isSuccess: boolean, msg?: any, rtnCode?: number): void {
    if (isSuccess) {
      this.router.navigate(['/']);
    } else if (msg) {
      if (rtnCode === 99) {
        this.systemDialogService.showErrorDialog(
          [
            {
              description: this.translateService.instant(
                'Login.EnterErrorThreeTimes'
              ),
            },
          ],
          'Error'
        );
      } else {
        this.systemDialogService.showErrorDialog(
          [
            {
              description: this.translateService.instant(
                'Login.AccountOrPasswordIncorrect'
              ),
            },
          ],
          'Error'
        );
      }
    } else {
      const loginFailTitle = this.translateService.instant('SigvLibrary.Error');
      const loginFailMessage = this.translateService.instant(
        'SigvLibrary.LoginFail'
      );
      this.systemDialogService.showErrorDialog(
        [
          {
            description: loginFailMessage,
          },
        ],
        loginFailTitle,
        () => {
          // do logout clear all login data and logout azure ad
          this.extranetAuthenticationService.logout();
        }
      );
    }
  }

  clickForgetPasswordHandler(): void {
    const { account } = this.loginFormModel;
    if (account) {
      this.passwordService
        .forgetPassword(account, '')
        .pipe(takeUntil(this.destroy$))
        .subscribe(({ status, msg, isSMS }) => {
          if (status) {
            if (isSMS == null) {
              this.toastService.success(
                this.translateService.instant('ForgotPassword.AllBeenSent')
              );
            } else {
              isSMS
                ? this.toastService.success(
                    this.translateService.instant(
                      'ForgotPassword.PhoneMailHadBeenSent'
                    )
                  )
                : this.toastService.success(
                    this.translateService.instant(
                      'ForgotPassword.MailHadBeenSent'
                    )
                  );
            }
          } else {
            this.systemDialogService.showErrorDialog(
              [
                {
                  description: msg ?? '',
                },
              ],
              'Error'
            );
          }
        });
    }
  }

  /**
   * 從忘記密碼頁面導過來，若有錯誤狀態需要跳錯誤訊息
   */
  checkStateWithQueryParam(): void {
    this.route.queryParams.subscribe((params: Params) => {
      if (params && params.state === 'invalidatingForgotPsw') {
        setTimeout(() => {
          this.systemDialogService.showErrorDialog(
            [
              {
                description: this.translateService.instant(
                  'Login.CheckIdentityInForgotPageFail'
                ),
              },
            ],
            'Error'
          );
        });
      }
    });
  }

  errored(event: any): void {
    console.warn(`reCAPTCHA error encountered \n `, event);
  }


  async forgetPassword(): Promise<void>{
    
    var fpType = await this.passwordService.getSystemParameter('ForgetPwdType').toPromise();

    if(fpType && fpType.data && fpType.rtnCode == 0){
      var otp = fpType.data.find(item => item.code == 'OTP');
      if(otp){
        const { account } = this.loginFormModel;
        if (account) {
          this.passwordService
            .checkAccount(account, '')
            .pipe(takeUntil(this.destroy$))
            .subscribe(({ status, msg, isSMS }) => {
              if (status) {
                this.router.navigateByUrl(`/forgotPasswordotp?account=${account}`);
              } else {
                this.systemDialogService.showErrorDialog(
                  [
                    {
                      description: msg ?? '',
                    },
                  ],
                  'Error'
                );
              }
            });
        }
        
      }
      else{
        this.clickForgetPasswordHandler();
      }
    }
    else {
      this.clickForgetPasswordHandler();
    }

   
  }


  forgetPasswordByOTP(): void{
    const { account } = this.loginFormModel;
    if (account) {
      this.passwordService
        .forgetPassword(account, this.otpType)
        .pipe(takeUntil(this.destroy$))
        .subscribe(({ status, msg, isSMS }) => {
          if (status) {
            if (isSMS == null) {
              this.toastService.success(
                this.translateService.instant('ForgotPassword.AllBeenSent')
              );
            } else {
              isSMS
                ? this.toastService.success(
                  this.translateService.instant(
                    'ForgotPassword.PhoneMailHadBeenSent'
                  )
                )
                : this.toastService.success(
                  this.translateService.instant(
                    'ForgotPassword.MailHadBeenSent'
                  )
                );
            }
          } else {
            this.systemDialogService.showErrorDialog(
              [
                {
                  description: msg ?? '',
                },
              ],
              'Error'
            );
          }
        });
    }
  }

  /**
   * account e-mail 驗證規則
   * 目前是由後端進行規則驗證，e-mail 跟 電話號碼 會去打 rbac
   * 先行註解，待日後有需要可以打開使用
   */
  // accountChangeHandler(): void {
  //   const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  //   this.isPasswordRequired = emailRegex.test(this.loginFormModel.account);
  // }
}
